import React, { useState, useEffect } from "react";
import ReactECharts from 'echarts-for-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LabelList,
  Cell,
  ReferenceLine,
} from "recharts";

interface WaterfallChartProps {
  data: number[];
  labels: string[];
  isBase100?: boolean;
}

const WaterfallChartECharts: React.FC<WaterfallChartProps> = ({
  data,
  labels,
  isBase100 = false,
}) => {

  // Step 1: Combine data and labels into one array for sorting
  let combined = data.map((value, index) => ({
    label: labels[index],
    value,
  }));

  // Step 2: Sort by absolute value, descending
  combined.sort((a, b) => Math.abs(b.value) - Math.abs(a.value));

  // Step 3: If more than 6 bars, group the rest into "Others"
  if (combined.length > 6) {
    const topFive = combined.slice(0, 6);
    const others = combined.slice(6);

    const othersValue = others.reduce((acc, curr) => acc + curr.value, 0);

    combined = [...topFive, { label: "Others", value: othersValue }];
  }

  // Step 4: Extract updated labels and data
  const filteredLabels = combined.map((d) => d.label);
  const filteredData = combined.map((d) => d.value);

  // Step 5: Compute offsets
  const offsets = filteredData.reduce((acc, value, index) => {
    acc.push(index === 0 ? 0 : acc[index - 1] + filteredData[index - 1]);
    return acc;
  }, [] as number[]);

  // Use filteredData and filteredLabels in the chart options
  // // Calculate offsets for waterfall logic
  // const offsets = data.reduce((acc, value, index) => {
  //   acc.push(index === 0 ? 0 : acc[index - 1] + data[index - 1]);
  //   return acc;
  // }, [] as number[]);

  const totalSum = data.reduce((acc, value) => acc + value, 0);

  // Format number like 1.2K, 3.5M, etc.
  const formatNumber = (value: number): string => {
    if (Math.abs(value) >= 1_000_000) {
      return `$${(value / 1_000_000).toFixed(1)}M`;
    } else if (Math.abs(value) >= 100_000) {
      return `$${(value / 1_000).toFixed(0)}K`;
    } else if (Math.abs(value) >= 1_000) {
      return `$${(value / 1_000).toFixed(1)}K`;
    } else {
      return `$${value}`;
    }
  };

  // Format x-axis labels
  // const formatXAxisLabel = (filteredLabels: string) =>
  //   filteredLabels
  //     .split("_")
  //     .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
  //     .join(" ");
  const formatXAxisLabel = (label: string) => {
    // Remove parentheses and their content
    let cleaned = label.replace(/\s*\(.*?\)/g, "");
  
    // Remove all instances of the word "spend" (case-insensitive, whole word only)
    cleaned = cleaned.replace(/\bspend\b/gi, "");
  
    // Remove extra spaces and underscores, normalize spacing
    cleaned = cleaned.replace(/[_\s]+/g, " ").trim();
  
    // Capitalize each word
    const words = cleaned
      .split(" ")
      .filter(Boolean) // remove empty strings if any
      .map(word => word.charAt(0).toUpperCase() + word.slice(1));
  
    const maxWordsPerLine = 1;
    const maxTotalWords = 3;
  
    let lines: string[] = [];
    for (let i = 0; i < words.length && i < maxTotalWords; i += maxWordsPerLine) {
      lines.push(words.slice(i, i + maxWordsPerLine).join(" "));
    }
  
    // Add ellipsis if words exceed maxTotalWords
    if (words.length > maxTotalWords) {
      const lastIndex = lines.length - 1;
      lines[lastIndex] += "...";
    }
  
    return lines.join("\n");
  };
  
  const option = {
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "shadow" },
      formatter: (params: any) => {
        const currentValue = params[1].data;
        const cumulativeValue = currentValue + offsets[params[1].dataIndex];
        const label = filteredLabels[params[1].dataIndex];
        return `
          ${label}<br/>
          Value: ${formatNumber(currentValue)}<br/>
          Cumulative: ${formatNumber(cumulativeValue)}
        `;
      },
    },
    grid: {
      left: "110px",
      right: "50px",
      bottom: "100px",
      top: "50px",
    },
    xAxis: {
      type: "category",
      data: filteredLabels,
      axisLabel: {
        rotate: 0,
        interval: 0,
        fontSize: 16,
        formatter: formatXAxisLabel,
      },
    },
    yAxis: {
      type: "value",
      axisLabel: {
        fontSize: 16,
        formatter: formatNumber,
      },
    },
    series: [
      {
        type: "bar",
        stack: "total",
        itemStyle: {
          color: "transparent",
        },
        data: offsets,
        barCategoryGap: "10%",
      },
      {
        name: "Value",
        type: "bar",
        stack: "total",
        label: {
          show: true,
          position: "top",
          formatter: (params: any) => {
            const rawValue = params.data;
            const percentage = isBase100
              ? ((rawValue / data[0]) * 100).toFixed(1)
              : ((rawValue / totalSum) * 100).toFixed(1);

            return `${formatNumber(rawValue)} (${percentage}%)`;
          },
          fontSize: 14,
          fontWeight: "bold",
        },
        itemStyle: {
          color: (params: any) => {
            const val = data[params.dataIndex];
            const totalBars = data.length;

            // Reversed index for right-to-left shading
            const reversedIndex = totalBars - 1 - params.dataIndex;

            // Lightness range from 70% (lightest) to 30% (darkest)
            const minLightness = 30;
            const maxLightness = 70;

            const lightness =
              maxLightness -
              ((maxLightness - minLightness) * reversedIndex) / (totalBars - 1);

            return val >= 0
              ? `hsl(220, 70%, ${lightness}%)` // Hue 220 = blue
              : "#69142f"; // fixed for negatives
          },
        },
        data: filteredData,
        barCategoryGap: "10%",
      },
    ],
  };


  return (
    <ReactECharts option={option} style={{ height: "700px", width: "100%" }} />
  );
};

export default WaterfallChartECharts;
